import React, { useState, useEffect, useRef } from 'react';
import './Navbar.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  TrainFront,
  Search,
  User,
  Menu,
  X,
  Bell,
  LogOut,
  Moon,
  Sun,
  ChevronDown,
  Sparkles,
  Home,
  MapPin,
  FileText,
  Radio,
  MoreHorizontal,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import toast from 'react-hot-toast';

const PRIMARY_LINKS = [
  { path: '/', label: 'Home', icon: Home },
  { path: '/trains', label: 'Trains', icon: Search },
  { path: '/pnr', label: 'PNR', icon: FileText },
  { path: '/live', label: 'Live', icon: Radio },
  { path: '/stations', label: 'Stations', icon: MapPin },
];

const MORE_LINKS = [
  { path: '/schedules', label: 'Schedules', isNew: false },
  { path: '/community', label: 'Community', isNew: true },
  { path: '/fare-calculator', label: 'Fare Calculator', isNew: false },
  { path: '/ecatering', label: 'E-Catering', isNew: true },
  { path: '/coach-layout', label: 'Coach Layout', isNew: false },
  { path: '/cancelled', label: 'Cancelled Trains', isNew: false },
  { path: '/retiring-rooms', label: 'Retiring Rooms', isNew: false },
  { path: '/chains', label: 'Connecting Trains', isNew: false },
  { path: '/tips', label: 'Travel Tips', isNew: false },
  { path: '/gallery', label: 'Heritage Gallery', isNew: false },
  { path: '/news', label: 'Rail News', isNew: false },
  { path: '/quiz', label: 'Rail Quiz', isNew: true },
];

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { isAuthenticated, user, logout } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    setProfileOpen(false);
    navigate('/');
  };

  return (
    <>
      {/* ── TOP APP BAR (desktop + mobile header) ─────────────── */}
      <header className="app-navbar">
        <div className="app-navbar__inner">

          {/* Brand */}
          <Link to="/" className="app-navbar__brand">
            <div className="app-navbar__logo">
              <TrainFront size={18} />
            </div>
            <span className="app-navbar__brand-name">YatriGo</span>
          </Link>

          {/* Desktop center nav */}
          <nav className="app-navbar__desktop-links">
            {PRIMARY_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`app-navbar__dlink ${location.pathname === link.path ? 'app-navbar__dlink--active' : ''}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right utilities */}
          <div className="app-navbar__actions">
            <Link to="/super-search" className="app-navbar__icon-btn" aria-label="Search">
              <Search size={18} />
            </Link>

            <button className="app-navbar__icon-btn" onClick={toggleTheme} aria-label="Toggle theme">
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>

            {isAuthenticated && (
              <button className="app-navbar__icon-btn app-navbar__icon-btn--bell" aria-label="Notifications">
                <Bell size={18} />
                <span className="app-navbar__bell-dot" />
              </button>
            )}

            {/* Profile */}
            {isAuthenticated ? (
              <div className="app-navbar__profile-wrap" ref={profileRef}>
                <button
                  className="app-navbar__avatar-btn"
                  onClick={() => setProfileOpen(!profileOpen)}
                >
                  <span className="app-navbar__avatar">
                    {user?.username?.charAt(0).toUpperCase() || 'U'}
                  </span>
                  <ChevronDown size={13} />
                </button>

                <AnimatePresence>
                  {profileOpen && (
                    <motion.div
                      className="app-navbar__dropdown"
                      initial={{ opacity: 0, y: 6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.97 }}
                      transition={{ duration: 0.12 }}
                    >
                      <div className="app-dropdown__user-row">
                        <div className="app-dropdown__big-avatar">
                          {user?.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="app-dropdown__user-info">
                          <p className="app-dropdown__user-name">{user?.username || 'Traveller'}</p>
                          <p className="app-dropdown__user-email">{user?.email || 'user@YatriGo.in'}</p>
                        </div>
                      </div>
                      <div className="app-dropdown__divider" />
                      <Link
                        to="/profile"
                        className="app-dropdown__item"
                        onClick={() => setProfileOpen(false)}
                      >
                        <User size={14} /> My Profile
                      </Link>
                      <button
                        className="app-dropdown__item app-dropdown__item--danger"
                        onClick={handleLogout}
                      >
                        <LogOut size={14} /> Sign Out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="app-navbar__auth-row">
                <Link to="/auth/login" className="app-navbar__btn-ghost">Sign In</Link>
                <Link to="/auth/register" className="app-navbar__btn-solid">Register</Link>
              </div>
            )}

            {/* Mobile hamburger (shows the More drawer) */}
            <button
              className="app-navbar__hamburger"
              onClick={() => setDrawerOpen(!drawerOpen)}
              aria-label="More"
            >
              {drawerOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

        </div>

        {/* Horizontal sub-nav pill strip — desktop only */}
        <div className="app-subnav">
          <div className="app-subnav__scroll">
            {MORE_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`app-subnav__pill ${location.pathname === link.path ? 'app-subnav__pill--active' : ''}`}
              >
                {link.isNew && <Sparkles size={11} />}
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </header>

      {/* ── FULL-SCREEN MOBILE DRAWER ──────────────────────────── */}
      <AnimatePresence>
        {drawerOpen && (
          <motion.div
            className="app-drawer"
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: '0%' }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
          >
            <div className="app-drawer__header">
              <span className="app-drawer__title">Menu</span>
              <button className="app-navbar__icon-btn" onClick={() => setDrawerOpen(false)}>
                <X size={20} />
              </button>
            </div>

            {!isAuthenticated && (
              <div className="app-drawer__auth-banner">
                <p className="app-drawer__auth-msg">Sign in for faster bookings & alerts</p>
                <div className="app-drawer__auth-btns">
                  <Link to="/auth/login" className="app-navbar__btn-ghost" onClick={() => setDrawerOpen(false)}>Sign In</Link>
                  <Link to="/auth/register" className="app-navbar__btn-solid" onClick={() => setDrawerOpen(false)}>Register</Link>
                </div>
              </div>
            )}

            <p className="app-drawer__group-label">More Services</p>
            <div className="app-drawer__grid">
              {MORE_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`app-drawer__tile ${location.pathname === link.path ? 'app-drawer__tile--active' : ''}`}
                  onClick={() => setDrawerOpen(false)}
                >
                  {link.isNew && <span className="app-drawer__new-badge">NEW</span>}
                  <span>{link.label}</span>
                </Link>
              ))}
            </div>

            {isAuthenticated && (
              <>
                <div className="app-dropdown__divider" style={{ margin: '16px 0' }} />
                <button className="app-drawer__logout" onClick={handleLogout}>
                  <LogOut size={15} /> Sign Out
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── BOTTOM TAB BAR (mobile only) ──────────────────────── */}
      <nav className="app-bottom-nav" role="navigation" aria-label="Main navigation">
        {PRIMARY_LINKS.map((link) => {
          const isActive = location.pathname === link.path;
          return (
            <Link key={link.path} to={link.path} className={`app-bottom-nav__tab ${isActive ? 'app-bottom-nav__tab--active' : ''}`}>
              <link.icon size={20} />
              <span>{link.label}</span>
            </Link>
          );
        })}
        <button
          className={`app-bottom-nav__tab ${drawerOpen ? 'app-bottom-nav__tab--active' : ''}`}
          onClick={() => setDrawerOpen(!drawerOpen)}
        >
          <MoreHorizontal size={20} />
          <span>More</span>
        </button>
      </nav>
    </>
  );
};

export default Navbar;